import type { RouteRecordRaw } from 'vue-router'

import type { AclAction } from '../acl/can'
import type { SoftwareCode } from '../stores/session'

import { manifestRoutes } from './manifest-routes'

export interface ManifestRouteEntry {
  path: string
  name?: string
  module: string
  wp: string
  legacyPath: string
  software?: string
  public?: true
  layout?: 'full'
  resource?: string
  action?: AclAction
  contentRenderer?: string
  navActiveLink?: string
}

const NotMigrated = () => import('../views/NotMigrated.vue')

/** `/:software/tasks/:id(\\d+)?` → `/:/tasks/:` — two patterns that match the same URLs compare equal. */
function shape(path: string): string {
  return path
    .replace(/:\w+(\([^)]*\))?[?*+]?/g, ':')
    .replace(/\/+$/, '')
    .toLowerCase()
}

function collect(routes: RouteRecordRaw[], paths: Set<string>, names: Set<string>, prefix = ''): void {
  for (const route of routes) {
    const path = route.path.startsWith('/') ? route.path : `${prefix}/${route.path}`
    paths.add(shape(path))
    if (typeof route.name === 'string') names.add(route.name)
    if (route.children) collect(route.children, paths, names, path)
  }
}

/**
 * One "Not migrated yet" page per proposed path of the route manifest that no module declares yet (owner decision,
 * wp/shell NOTES): every legacy redirect lands on a real route and the manifest test passes. A module that declares the
 * path (or the legacy route name) replaces its placeholder by existing — nothing to edit here.
 *
 * Each placeholder carries the meta the guard needs (D-21): the software and, when the legacy `meta.module` is an ACL
 * key, the resource and action, so the software and ACL checks already apply.
 */
export function notMigratedRoutes(
  declared: RouteRecordRaw[],
  entries: ManifestRouteEntry[] = manifestRoutes,
): RouteRecordRaw[] {
  const paths = new Set<string>()
  const names = new Set<string>()
  collect(declared, paths, names)
  return entries
    .filter((entry) => !paths.has(shape(entry.path)) && !(entry.name && names.has(entry.name)))
    .map((entry) => ({
      path: entry.path,
      ...(entry.name ? { name: entry.name } : {}),
      component: NotMigrated,
      meta: {
        ...(entry.software ? { software: entry.software as SoftwareCode | 'common' } : {}),
        ...(entry.public ? { public: true } : {}),
        ...(entry.layout ? { layout: entry.layout } : {}),
        ...(entry.resource ? { resource: entry.resource, action: entry.action ?? 'read' } : {}),
        ...(entry.contentRenderer === 'sidebar-left' ? { contentRenderer: 'sidebar-left' as const } : {}),
        ...(entry.navActiveLink ? { navActiveLink: entry.navActiveLink } : {}),
        moduleId: entry.module,
        notMigrated: { wp: entry.wp, module: entry.module, legacyPath: entry.legacyPath },
      },
    }))
}
